import { useEffect, useState, useCallback } from "react";
import { api, formatApiError } from "@/lib/api";
import { toast } from "sonner";
import { Rocket, Loader2, Save, Crosshair, BookOpen, BarChart3, ChevronDown, Layers, Gift } from "lucide-react";
import PrizeBoxPanel from "@/components/PrizeBoxPanel";
import PersonalVaultPanel from "@/components/PersonalVaultPanel";
import { zoomCard } from "@/components/CardZoom";

const EXPLAINER = [
  ["1 · Minting", "A player pays to mint their AI Sports Card (5 BLC default, free for VIP/ACE/GOAT — set in AI Sports Card Pricing). Every minted card automatically joins the Vault's pack pool. Rarity comes from their tier: GOAT = Gold, ACE = Holo, others Standard. You can also forge BULLPEN EXCLUSIVE cards from your own photos in the Card Forge below — they enter the pool at their own (rarer) odds, and if you feature a player on one, royalties split 50/50 between them and the treasury."],
  ["2 · Packs (your main revenue)", "Players buy sealed packs with BLC. 100% of the pack price goes to your treasury (BLC-BULLPEN). From that, a royalty (default 10% of the pack price, split across the pulled cards) is paid to each pulled card's player — the rest stays with the house. Rarity odds per card slot are set below."],
  ["3 · Bounties (the prize engine)", "You attach a BLC prize + number of winning copies to any card below. When a player pulls a bounty copy from a pack, the prize is paid INSTANTLY from the Red Vault (bonus tokens) and announced on the Community feed. Champion bounties are AUTOMATIC: the moment a tournament is completed, every division champion's card gets a bounty (prize + copies set in the controls below; if they haven't minted a card yet, the bounty activates the moment they do). You control total exposure: seed less in bounties than packs bring in — the scratch-off model."],
  ["4 · Hot Cards", "Set a 2x/5x multiplier on any card — it shows a flame badge everywhere, signalling collectible value and driving marketplace demand. No automatic payout; it's pure status you control."],
  ["5 · Marketplace", "Players list duplicates at their own price. On every sale: house fee (default 10%) → treasury, royalty (default 10%) → the card's player, remainder → seller. Cards of hot players and champions naturally appreciate — the 'player stock market'."],
  ["6 · Set rewards", "Collection sets (First Five, Squad Collector, Holo Hunter, Gold Rush) pay small one-time bonus-token rewards from the Red Vault. They keep casual players ripping packs."],
  ["Money flow summary", "IN → treasury: mint fees, 100% of pack sales, 10% of every marketplace sale. OUT ← Red Vault (bonus tokens you mint for promos): bounty prizes, set rewards. OUT ← treasury: pack royalties to players. Keep the Red Vault topped up from the Bullion Coin section."],
];

export default function VaultCommandCenter() {
  const [ov, setOv] = useState(null);
  const [cfg, setCfg] = useState(null);
  const [cards, setCards] = useState([]);
  const [edits, setEdits] = useState({});
  const [busy, setBusy] = useState(false);
  const [launching, setLaunching] = useState(false);
  const [plan, setPlan] = useState(null);
  const [showExplainer, setShowExplainer] = useState(false);
  const [giftFee, setGiftFee] = useState("");
  const [backfilling, setBackfilling] = useState(false);
  const [hrBackfilling, setHrBackfilling] = useState(false);

  const runHouseBackfill = async () => {
    setHrBackfilling(true);
    try {
      const { data } = await api.post("/admin/vault/house-reserve/backfill", {}, { timeout: 120000 });
      toast.success(data.created ? `${data.created} house cop${data.created === 1 ? "y" : "ies"} minted into your collection` : "Every exclusive already has a house copy — nothing to do");
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
    finally { setHrBackfilling(false); }
  };
  const [regenning, setRegenning] = useState(null);
  const [backEdit, setBackEdit] = useState(null);
  const [perkClaims, setPerkClaims] = useState([]);

  const loadPerkClaims = () => api.get("/admin/vault/perk-claims").then(({ data }) => setPerkClaims(data)).catch(() => {});
  useEffect(() => { loadPerkClaims(); }, []);

  const deliverPerk = async (p) => {
    try {
      await api.post(`/admin/vault/perk-claims/${p.id}/deliver`);
      toast.success(`Marked delivered — ${p.winner_name} has been notified`);
      loadPerkClaims();
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
  };

  const openBackEdit = async (c) => {
    try {
      const { data } = await api.get(`/cards/${c.id}/back`, { timeout: 90000 });
      setBackEdit({ id: c.id, name: c.subject_name, fact: data.fact || "",
                    stats: (data.stats || []).map(([l, v]) => `${l} | ${v}`).join("\n") });
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
  };

  const saveBackEdit = async () => {
    const stats = backEdit.stats.split("\n").map((l) => l.split("|").map((s) => s.trim()))
      .filter((p) => p.length >= 2 && p[0] && p[1]).map((p) => [p[0], p[1]]);
    try {
      await api.put(`/admin/cards/${backEdit.id}/back`, { fact: backEdit.fact, stats });
      toast.success("Card-back fact updated");
      setBackEdit(null);
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
  };

  const regenBack = async (c) => {
    setRegenning(c.id);
    try {
      const { data } = await api.post(`/admin/cards/${c.id}/regenerate-back`, {}, { timeout: 90000 });
      toast.success(`New card-back fact: "${data.fact.slice(0, 110)}…"`);
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
    finally { setRegenning(null); }
  };

  const setShowcaseTag = async (c, tag) => {
    try {
      await api.put(`/admin/vault/cards/${c.id}/showcase`, { tag });
      setCards((prev) => prev.map((x) => (x.id === c.id ? { ...x, showcase_tag: tag } : x)));
      toast.success(tag ? `"${c.subject_name}" is on display — ${tag}` : `"${c.subject_name}" removed from the Showcase Window`);
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
  };

  const runBackfill = async () => {
    setBackfilling(true);
    try {
      const { data } = await api.post("/admin/vault/security/backfill", {}, { timeout: 120000 });
      toast.success(`Security stamped on ${data.secured} copies${data.failed ? ` (${data.failed} failed)` : ""}`);
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
    finally { setBackfilling(false); }
  };

  const load = useCallback(() => {
    api.get("/admin/vault/overview").then(({ data }) => setOv(data)).catch(() => {});
    api.get("/vault/config").then(({ data }) => setCfg(data)).catch(() => {});
    api.get("/admin/vault/cards").then(({ data }) => setCards(data)).catch(() => {});
    api.get("/vault/transfer-fee").then(({ data }) => setGiftFee(data.fee_coins)).catch(() => {});
  }, []);
  useEffect(load, [load]);

  const saveSettings = async () => {
    setBusy(true);
    try {
      const { data } = await api.put("/admin/vault/settings", {
        enabled: cfg.enabled,
        small_price: Number(cfg.packs.small.price) || 0, large_price: Number(cfg.packs.large.price) || 0,
        gold_odds: Number(cfg.odds.gold) || 0, holo_odds: Number(cfg.odds.holo) || 0,
        exclusive_odds: Number(cfg.odds.exclusive) || 0,
        elite_odds: Number(cfg.odds.elite) || 0, rare_odds: Number(cfg.odds.rare) || 0,
        common_odds: Number(cfg.odds.common) || 0,
        legendary_odds: Number(cfg.odds.legendary) || 0, icon_odds: Number(cfg.odds.icon) || 0,
        royalty_pct: Number(cfg.royalty_pct) || 0, market_fee_pct: Number(cfg.market_fee_pct) || 0,
        champion_bounty_prize: Number(cfg.champion_bounty_prize) || 0,
        champion_bounty_copies: Number(cfg.champion_bounty_copies) || 0,
        house_edge_cap_pct: Number(cfg.house_edge_cap_pct) || 0,
        slab_fee_blc: Number(cfg.slab_fee_blc ?? 15) || 0,
      });
      await api.put("/admin/vault/transfer-fee", { fee_coins: Number(giftFee) || 0 });
      setCfg(data);
      toast.success("Card Vault settings saved");
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
    finally { setBusy(false); }
  };

  const launch = async () => {
    setLaunching(true);
    try {
      const { data } = await api.post("/admin/vault/launch");
      setPlan(data.operation_plan);
      toast.success("The Card Vault is LIVE!");
      load();
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
    finally { setLaunching(false); }
  };

  const saveBounty = async (c) => {
    const e = edits[c.id] || {};
    try {
      await api.put(`/admin/vault/cards/${c.id}/bounty`, {
        prize: Number(e.prize ?? c.bounty_prize) || 0,
        copies: Number(e.copies ?? c.bounty_copies) || 0,
        hot_multiplier: Number(e.hot ?? c.hot_multiplier) || 1,
        perks: (e.perks ?? c.bounty_perks) || "",
      });
      toast.success(`Updated ${c.subject_name}'s card`);
      load();
    } catch (err) { toast.error(formatApiError(err.response?.data?.detail)); }
  };

  if (!ov || !cfg) return null;
  const s = ov.stats;
  const STAT = [
    ["Cards Minted", s.cards_minted, `+${s.cards_minted_7d} this week`, "#A3E635"],
    ["Packs Opened", s.packs_opened, `${s.pack_revenue} BLC revenue`, "#F87171"],
    ["Copies Circulating", s.copies_in_circulation, `${s.listings_active} listed`, "#22D3EE"],
    ["Royalties Paid", `${s.royalties_paid} BLC`, "to card owners", "#C084FC"],
    ["Bounties Paid", `${s.bounties_paid} BLC`, `${s.bounties_hit} hit`, "#FACC15"],
    ["Market Volume", `${s.market_volume} BLC`, `${s.market_sales} sales`, "#4ADE80"],
  ];

  return (
    <div className="space-y-6" data-testid="vault-command-center">
      {/* Launch */}
      <div className={`border p-5 ${ov.enabled ? "border-emerald-500/50 bg-emerald-500/5" : "border-pb-red/60 bg-pb-red/5"}`}>
        <div className="flex flex-wrap items-center justify-between gap-4">
          <div>
            <div className="font-display font-black text-xl uppercase flex items-center gap-2">
              <Layers className="w-5 h-5 text-pb-red" /> Card Vault — {ov.enabled ? <span className="text-emerald-400">LIVE</span> : <span className="text-pb-red">OFFLINE</span>}
            </div>
            <p className="text-xs text-zinc-500 mt-1 max-w-xl">One button runs the whole operation: enables the Vault tab for every member, activates packs/bounties/marketplace, posts the launch announcement to the feed, notifies all members, and prints your operation plan below.</p>
          </div>
          <button data-testid="vault-launch-btn" onClick={launch} disabled={launching}
            className="flex items-center gap-2 bg-pb-red text-black font-black uppercase tracking-widest px-8 py-3.5 text-sm hover:bg-[#BEF264] active:scale-95 transition-all disabled:opacity-50">
            {launching ? <Loader2 className="w-4 h-4 animate-spin" /> : <Rocket className="w-4 h-4" />}
            {ov.enabled ? "Re-run Operation Plan" : "Launch the Card Vault"}
          </button>
        </div>
        {plan && (
          <ol className="mt-4 space-y-1.5 border-t border-pb-border pt-4" data-testid="vault-operation-plan">
            {plan.map((p, i) => <li key={i} className="text-xs text-zinc-300 flex gap-2"><span className="text-pb-red font-black shrink-0">{i + 1}.</span>{p}</li>)}
          </ol>
        )}
      </div>

      {/* Explainer */}
      <div className="border border-pb-border bg-pb-surface">
        <button data-testid="vault-explainer-toggle" onClick={() => setShowExplainer(!showExplainer)}
          className="w-full flex items-center justify-between p-4 text-left">
          <span className="flex items-center gap-2 font-display font-bold uppercase"><BookOpen className="w-4 h-4 text-pb-cyan" /> How the entire system works — mint → pack → bounty → sale</span>
          <ChevronDown className={`w-4 h-4 text-zinc-500 transition-transform ${showExplainer ? "rotate-180" : ""}`} />
        </button>
        {showExplainer && (
          <div className="px-4 pb-4 space-y-3" data-testid="vault-explainer-body">
            {EXPLAINER.map(([t, body]) => (
              <div key={t}>
                <div className="text-xs font-black uppercase tracking-widest text-pb-red mb-0.5">{t}</div>
                <p className="text-xs text-zinc-400 leading-relaxed">{body}</p>
              </div>
            ))}
          </div>
        )}
      </div>

      {/* House Law */}
      {ov.house && (
        <div className="border border-emerald-500/40 bg-emerald-500/5 p-5" data-testid="vault-house-law">
          <div className="flex flex-wrap items-center justify-between gap-3 mb-3">
            <div className="font-display font-black uppercase text-lg text-emerald-400">🏛 House Never Loses — Law Active</div>
            <span className="text-[10px] uppercase tracking-widest text-zinc-500">Lifetime payouts hard-capped at {ov.house.cap_pct}% of vault income</span>
          </div>
          <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
            {[["Vault Income", `${ov.house.income} BLC`, "packs + mint, market & gift fees", "#A3E635"],
              ["Paid Out (value)", `${ov.house.payouts} BLC`, "royalties + bounties + set rewards", "#F87171"],
              ["House Edge Kept", `${ov.house.edge_kept} BLC`, "income minus payouts — never negative", "#4ADE80"],
              ["Payout Headroom", `${ov.house.headroom} BLC`, "bounties auto-pause when this hits 0", "#22D3EE"]].map(([l, v, sub, color]) => (
              <div key={l} className="bg-pb-elevated border border-pb-border p-3">
                <div className="text-[9px] uppercase tracking-[0.2em] text-zinc-500 mb-1">{l}</div>
                <div className="font-display font-black text-lg" style={{ color }}>{v}</div>
                <div className="text-[9px] text-zinc-600">{sub}</div>
              </div>
            ))}
          </div>
          <p className="text-[10px] text-zinc-500 mt-3">The law is enforced at the moment of every pull: if a bounty payout would push lifetime payouts past the cap, the bounty simply isn't claimed on that pull (it stays live for later) and set-reward claims pause until more income comes in. Bonus-coin prizes are counted at their true value (10 bonus = 1 BLC).</p>
        </div>
      )}

      {/* Report */}
      <div className="border border-pb-border bg-pb-surface p-5" data-testid="vault-report">
        <div className="flex items-center gap-2 mb-4"><BarChart3 className="w-4 h-4 text-pb-red" /><span className="font-display font-bold uppercase">Card Vault Report</span></div>
        <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-6 gap-3 mb-5">
          {STAT.map(([l, v, sub, color]) => (
            <div key={l} className="bg-pb-elevated border border-pb-border p-3">
              <div className="text-[9px] uppercase tracking-[0.2em] text-zinc-500 mb-1">{l}</div>
              <div className="font-display font-black text-lg" style={{ color }}>{v}</div>
              <div className="text-[9px] text-zinc-600">{sub}</div>
            </div>
          ))}
        </div>
        {ov.new_cards.length > 0 && (
          <div className="mb-5">
            <div className="text-[10px] uppercase tracking-[0.2em] text-zinc-500 mb-2">Newest additions to the pool</div>
            <div className="flex gap-2 overflow-x-auto pb-1">
              {ov.new_cards.map((c) => (
                <img key={c.id} src={c.url} alt={c.subject_name} title={c.subject_name} className="w-16 aspect-[3/4] object-contain bg-black border border-pb-border shrink-0 cursor-zoom-in"
                  onClick={() => zoomCard(c.url, c.subject_name)} />
              ))}
            </div>
          </div>
        )}
        <div className="text-[10px] uppercase tracking-[0.2em] text-zinc-500 mb-2">Recent activity</div>
        <div className="max-h-52 overflow-y-auto divide-y divide-pb-border/40 border border-pb-border" data-testid="vault-events">
          {ov.recent_events.length === 0 && <div className="p-3 text-xs text-zinc-600">No vault activity yet.</div>}
          {ov.recent_events.map((e, i) => (
            <div key={i} className="flex items-center justify-between px-3 py-2 text-xs">
              <span className="text-zinc-300 truncate pr-3">{e.note}</span>
              <span className="shrink-0 text-zinc-500">{e.created_at ? new Date(e.created_at).toLocaleString() : ""}</span>
            </div>
          ))}
        </div>
      </div>

      {/* Settings */}
      <div className="border border-pb-border bg-pb-surface p-5" data-testid="vault-settings">
        <div className="font-display font-bold uppercase mb-4">Regulation Controls</div>
        <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-6 gap-4 mb-4">
          {[["Starter pack (BLC)", cfg.packs.small.price, (v) => setCfg({ ...cfg, packs: { ...cfg.packs, small: { ...cfg.packs.small, price: v } } }), "small-price"],
            ["Rip pack (BLC)", cfg.packs.large.price, (v) => setCfg({ ...cfg, packs: { ...cfg.packs, large: { ...cfg.packs.large, price: v } } }), "large-price"],
            ["Gold odds %", cfg.odds.gold, (v) => setCfg({ ...cfg, odds: { ...cfg.odds, gold: v } }), "gold-odds"],
            ["Holo odds %", cfg.odds.holo, (v) => setCfg({ ...cfg, odds: { ...cfg.odds, holo: v } }), "holo-odds"],
            ["Exclusive odds %", cfg.odds.exclusive ?? 3, (v) => setCfg({ ...cfg, odds: { ...cfg.odds, exclusive: v } }), "exclusive-odds"],
            ["Icon odds %", cfg.odds.icon ?? 2, (v) => setCfg({ ...cfg, odds: { ...cfg.odds, icon: v } }), "icon-odds"],
            ["Legendary odds %", cfg.odds.legendary ?? 4, (v) => setCfg({ ...cfg, odds: { ...cfg.odds, legendary: v } }), "legendary-odds"],
            ["Elite odds %", cfg.odds.elite ?? 2, (v) => setCfg({ ...cfg, odds: { ...cfg.odds, elite: v } }), "elite-odds"],
            ["Rare odds %", cfg.odds.rare ?? 10, (v) => setCfg({ ...cfg, odds: { ...cfg.odds, rare: v } }), "rare-odds"],
            ["Common odds %", cfg.odds.common ?? 0, (v) => setCfg({ ...cfg, odds: { ...cfg.odds, common: v } }), "common-odds"],
            ["Royalty %", cfg.royalty_pct, (v) => setCfg({ ...cfg, royalty_pct: v }), "royalty"],
            ["Market fee %", cfg.market_fee_pct, (v) => setCfg({ ...cfg, market_fee_pct: v }), "market-fee"],
            ["Champ bounty (BLC)", cfg.champion_bounty_prize ?? 10, (v) => setCfg({ ...cfg, champion_bounty_prize: v }), "champ-prize"],
            ["Champ bounty copies", cfg.champion_bounty_copies ?? 3, (v) => setCfg({ ...cfg, champion_bounty_copies: v }), "champ-copies"],
            ["House edge cap %", cfg.house_edge_cap_pct ?? 80, (v) => setCfg({ ...cfg, house_edge_cap_pct: v }), "house-cap"],
            ["Slab & grade fee (BLC)", cfg.slab_fee_blc ?? 15, (v) => setCfg({ ...cfg, slab_fee_blc: v }), "slab-fee"]].map(([label, val, set, tid]) => (
            <div key={tid}>
              <label className="block text-[9px] uppercase tracking-[0.2em] text-zinc-500 mb-1.5 font-bold">{label}</label>
              <input data-testid={`vault-setting-${tid}`} type="number" min={0} step="0.5" value={val}
                onChange={(e) => set(e.target.value)}
                className="w-full bg-pb-elevated border border-pb-border px-3 py-2 text-white font-mono text-sm focus:outline-none focus:border-pb-red" />
            </div>
          ))}
          <div>
            <label className="block text-[9px] uppercase tracking-[0.2em] text-zinc-500 mb-1.5 font-bold">Card gift fee (BLC)</label>
            <input data-testid="vault-setting-gift-fee" type="number" min={0} step="0.5" value={giftFee}
              onChange={(e) => setGiftFee(e.target.value)}
              className="w-full bg-pb-elevated border border-pb-border px-3 py-2 text-white font-mono text-sm focus:outline-none focus:border-pb-red" />
          </div>
        </div>
        <button data-testid="vault-settings-save" onClick={saveSettings} disabled={busy}
          className="flex items-center gap-1.5 bg-pb-red text-black font-bold uppercase tracking-widest px-6 py-2.5 text-xs hover:bg-[#BEF264] active:scale-95 transition-all disabled:opacity-50">
          {busy ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Save className="w-3.5 h-3.5" />} Save Controls
        </button>
        <div className="mt-4 pt-4 border-t border-pb-border flex flex-wrap items-center gap-3">
          <button data-testid="vault-security-backfill" onClick={runBackfill} disabled={backfilling}
            className="flex items-center gap-1.5 border border-pb-cyan/60 text-pb-cyan font-bold uppercase tracking-widest px-5 py-2.5 text-xs hover:bg-pb-cyan/10 transition-colors disabled:opacity-50">
            {backfilling ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Save className="w-3.5 h-3.5" />}
            Stamp security on all existing copies
          </button>
          <span className="text-[10px] text-zinc-500 max-w-md">New copies are auto-stamped (serial, micro-text, guilloché pattern, QR + invisible watermark). This retrofits every copy minted before the security system launched.</span>
        </div>
        <div className="mt-3 pt-3 border-t border-pb-border/50 flex flex-wrap items-center gap-3">
          <button data-testid="vault-house-reserve-backfill" onClick={runHouseBackfill} disabled={hrBackfilling}
            className="flex items-center gap-1.5 border border-amber-400/60 text-amber-300 font-bold uppercase tracking-widest px-5 py-2.5 text-xs hover:bg-amber-400/10 transition-colors disabled:opacity-50">
            {hrBackfilling ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Save className="w-3.5 h-3.5" />}
            Claim house copies of all exclusives
          </button>
          <span className="text-[10px] text-zinc-500 max-w-md">The house always holds — every NEW exclusive auto-mints one copy into your collection. This backfills a house copy of every exclusive forged before the rule existed.</span>
        </div>
      </div>

      {/* Personal vault */}
      <PersonalVaultPanel />

      {/* Prize boxes */}
      <PrizeBoxPanel />

      {/* Perks owed */}
      {perkClaims.length > 0 && (
        <div className="border border-amber-400/40 bg-amber-400/5 p-5" data-testid="vault-perks-owed">
          <div className="flex items-center gap-2 mb-1"><Gift className="w-4 h-4 text-amber-400" /><span className="font-display font-bold uppercase">Perks Owed — Bounty Fulfillment</span></div>
          <p className="text-xs text-zinc-500 mb-4">Every bounty pull that came with extra perks lands here. Deliver the perk, then mark it done — the winner gets notified automatically.</p>
          <div className="max-h-72 overflow-y-auto divide-y divide-pb-border/40">
            {perkClaims.map((p) => (
              <div key={p.id} className="flex flex-wrap items-center gap-3 py-2.5" data-testid={`perk-claim-${p.id}`}>
                <img src={p.card_url} alt="" className="w-8 aspect-[3/4] object-contain bg-black border border-pb-border cursor-zoom-in"
                  onClick={() => zoomCard(p.card_url, p.subject_name)} />
                <div className="flex-1 min-w-[160px]">
                  <div className="text-xs font-semibold">{p.winner_name} <span className="text-zinc-500 font-normal">pulled</span> {p.card_name}</div>
                  <div className="text-[11px] text-amber-300 font-bold">🎁 {p.perks}
                    {p.redemption_requested && !p.delivered && (
                      <span className="ml-2 text-[8px] font-black uppercase tracking-widest border border-pb-cyan/60 text-pb-cyan px-1.5 py-0.5" data-testid={`perk-requested-${p.id}`}>Player requested redemption</span>
                    )}
                  </div>
                  <div className="text-[9px] text-zinc-600">{(p.created_at || "").slice(0, 10)}</div>
                </div>
                {p.delivered ? (
                  <span className="text-[9px] font-black uppercase tracking-widest text-emerald-400" data-testid={`perk-delivered-${p.id}`}>✓ Delivered {(p.delivered_at || "").slice(0, 10)}</span>
                ) : (
                  <button data-testid={`perk-deliver-${p.id}`} onClick={() => deliverPerk(p)}
                    className="text-[9px] font-black uppercase tracking-widest bg-amber-400 text-black px-3 py-2 hover:bg-amber-300 transition-colors">
                    Mark delivered
                  </button>
                )}
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Bounty manager */}
      <div className="border border-pb-border bg-pb-surface p-5" data-testid="vault-bounty-manager">
        <div className="flex items-center gap-2 mb-1"><Crosshair className="w-4 h-4 text-pb-red" /><span className="font-display font-bold uppercase">Bounty & Hot Card Manager</span></div>
        <p className="text-xs text-zinc-500 mb-4">Attach a BLC prize (paid from the Red Vault when pulled) and winning-copy count to any minted card, or set a Hot multiplier for status. Set prize 0 to remove a bounty.</p>
        <div className="max-h-96 overflow-y-auto divide-y divide-pb-border/40">
          {cards.map((c) => {
            const e = edits[c.id] || {};
            return (
              <div key={c.id} className="flex flex-wrap items-center gap-3 py-3" data-testid={`bounty-row-${c.id}`}>
                <img src={c.url} alt="" className="w-10 aspect-[3/4] object-contain bg-black border border-pb-border cursor-zoom-in"
                  onClick={() => zoomCard(c.url, c.subject_name)} />
                <div className="w-32 min-w-0">
                  <div className="text-xs font-semibold truncate">{c.subject_name}</div>
                  <div className="text-[9px] uppercase tracking-widest text-zinc-500">{c.rarity} · {c.copies_in_circulation} in circulation</div>
                  {c.bounty_prize > 0 && <div className="text-[9px] text-pb-red font-bold">{c.bounty_claimed}/{c.bounty_copies} bounties claimed</div>}
                </div>
                {[["Prize BLC", "prize", c.bounty_prize], ["Copies", "copies", c.bounty_copies], ["Hot ×", "hot", c.hot_multiplier]].map(([lbl, k, cur]) => (
                  <div key={k}>
                    <label className="block text-[8px] uppercase tracking-widest text-zinc-600">{lbl}</label>
                    <input data-testid={`bounty-${k}-${c.id}`} type="number" min={0} step={k === "hot" ? 1 : 0.5} value={e[k] ?? cur}
                      onChange={(ev) => setEdits({ ...edits, [c.id]: { ...e, [k]: ev.target.value } })}
                      className="w-20 bg-pb-elevated border border-pb-border px-2 py-1.5 text-white font-mono text-xs focus:outline-none focus:border-pb-red" />
                  </div>
                ))}
                <div className="min-w-[180px] flex-1">
                  <label className="block text-[8px] uppercase tracking-widest text-zinc-600">Comes with (perks shown to hunters)</label>
                  <input data-testid={`bounty-perks-input-${c.id}`} type="text" maxLength={200} value={e.perks ?? c.bounty_perks ?? ""}
                    placeholder="e.g. Free tournament entry"
                    onChange={(ev) => setEdits({ ...edits, [c.id]: { ...e, perks: ev.target.value } })}
                    className="w-full bg-pb-elevated border border-pb-border px-2 py-1.5 text-white text-xs focus:outline-none focus:border-amber-400" />
                </div>
                <button data-testid={`bounty-save-${c.id}`} onClick={() => saveBounty(c)}
                  className="text-[9px] font-black uppercase tracking-widest bg-pb-elevated border border-pb-border px-3 py-2 hover:border-pb-red hover:text-pb-red transition-colors">
                  Save
                </button>
                <button data-testid={`regen-back-${c.id}`} onClick={() => regenBack(c)} disabled={regenning === c.id}
                  title="Rewrite this card's flip-side AI fact"
                  className="text-[9px] font-black uppercase tracking-widest bg-pb-elevated border border-pb-border px-3 py-2 hover:border-pb-cyan hover:text-pb-cyan transition-colors disabled:opacity-50">
                  {regenning === c.id ? "Writing…" : "↻ Fact"}
                </button>
                <button data-testid={`edit-back-${c.id}`} onClick={() => (backEdit?.id === c.id ? setBackEdit(null) : openBackEdit(c))}
                  title="Manually edit this card's flip-side fact"
                  className={`text-[9px] font-black uppercase tracking-widest bg-pb-elevated border px-3 py-2 transition-colors ${backEdit?.id === c.id ? "border-amber-400 text-amber-300" : "border-pb-border hover:border-amber-400 hover:text-amber-300"}`}>
                  ✎ Edit fact
                </button>
                <div>
                  <label className="block text-[8px] uppercase tracking-widest text-zinc-600">Showcase Window</label>
                  <select data-testid={`showcase-select-${c.id}`} value={c.showcase_tag || ""}
                    onChange={(ev) => setShowcaseTag(c, ev.target.value)}
                    className={`bg-pb-elevated border text-xs px-2 py-1.5 focus:outline-none ${c.showcase_tag ? "border-[#A3E635]/70 text-[#A3E635]" : "border-pb-border text-zinc-400"}`}>
                    <option value="">Hidden</option>
                    <option value="In Packs Now">In Packs Now</option>
                    <option value="Sneak Peek">Sneak Peek</option>
                    <option value="Coming Soon">Coming Soon</option>
                    <option value="Grail Watch">Grail Watch</option>
                  </select>
                </div>
                {backEdit?.id === c.id && (
                  <div className="w-full border border-amber-400/40 bg-amber-400/5 p-3 space-y-2" data-testid={`back-edit-panel-${c.id}`}>
                    <div className="text-[9px] font-black uppercase tracking-widest text-amber-300">Editing the back of "{backEdit.name}"</div>
                    <div>
                      <label className="block text-[8px] uppercase tracking-widest text-zinc-500 mb-1">"Did You Know" fact</label>
                      <textarea data-testid={`back-edit-fact-${c.id}`} value={backEdit.fact} rows={3}
                        onChange={(e) => setBackEdit({ ...backEdit, fact: e.target.value })}
                        className="w-full bg-pb-elevated border border-pb-border px-3 py-2 text-xs text-white leading-relaxed focus:outline-none focus:border-amber-400" />
                    </div>
                    <div>
                      <label className="block text-[8px] uppercase tracking-widest text-zinc-500 mb-1">Back stats — one per line as Label | Value</label>
                      <textarea data-testid={`back-edit-stats-${c.id}`} value={backEdit.stats} rows={3}
                        onChange={(e) => setBackEdit({ ...backEdit, stats: e.target.value })}
                        className="w-full bg-pb-elevated border border-pb-border px-3 py-2 text-xs text-white font-mono focus:outline-none focus:border-amber-400" />
                    </div>
                    <div className="flex gap-2">
                      <button data-testid={`back-edit-save-${c.id}`} onClick={saveBackEdit}
                        className="text-[9px] font-black uppercase tracking-widest bg-amber-400 text-black px-4 py-2 hover:bg-amber-300 transition-colors">Save fact</button>
                      <button data-testid={`back-edit-cancel-${c.id}`} onClick={() => setBackEdit(null)}
                        className="text-[9px] font-black uppercase tracking-widest border border-pb-border text-zinc-400 px-4 py-2 hover:border-zinc-500">Cancel</button>
                    </div>
                  </div>
                )}
              </div>
            );
          })}
          {cards.length === 0 && <div className="py-4 text-xs text-zinc-600">No cards minted yet — the pool fills as players mint sports cards.</div>}
        </div>
      </div>
    </div>
  );
}
